import { useCallback } from "react";
import { StringTransformer } from "@/features/ui/helpers/string-transformer";
import {
  LineSheetSetStatuses,
  LineSheetSetTypes,
} from "@/features/line-sheet-sets/line-sheet-set.types";
import { usePersistedQuery } from "@/features/order-sheet-sets/hooks/use-persisted-query";
import { ListLineSheetSetsRequest } from "@/features/line-sheet-sets/hooks/use-list-line-sheet-sets";

export type PersistedLegacyLineSheetSetsListPageQuery = Omit<
  ListLineSheetSetsRequest,
  "orderBy" | "pageSize" | "id__in" | "season__eq"
>;

export default function usePersistedLineSheetSetListQuery() {
  const { value, set, clear } =
    usePersistedQuery<PersistedLegacyLineSheetSetsListPageQuery>({
      entries: {
        pageNumber: {
          parse: StringTransformer.parseId,
          stringify: StringTransformer.stringifyId,
        },
        status__in: {
          parse:
            StringTransformer.withParseStringLiterals(LineSheetSetStatuses),
          stringify: StringTransformer.stringifyStringLiterals,
        },
        status__eq: {
          parse: StringTransformer.withParseStringLiteral(LineSheetSetStatuses),
          stringify: StringTransformer.stringifyStringLiteral,
        },
        isValid__eq: {
          parse: StringTransformer.parseBoolean,
          stringify: StringTransformer.stringifyBoolean,
        },
        validUntil__gt: {
          parse: StringTransformer.parseDate,
          stringify: StringTransformer.stringifyDate,
        },
        type__eq: {
          parse: StringTransformer.withParseStringLiteral(LineSheetSetTypes),
          stringify: StringTransformer.stringifyStringLiteral,
        },
        "issuedByCompany.id__eq": {
          parse: StringTransformer.parseId,
          stringify: StringTransformer.stringifyId,
        },
        "issuedByCompany.id__in": {
          parse: StringTransformer.parseIds,
          stringify: StringTransformer.stringifyIds,
        },
        "madeByCompany.id__eq": {
          parse: StringTransformer.parseId,
          stringify: StringTransformer.stringifyId,
        },
        "madeByCompany.id__in": {
          parse: StringTransformer.parseIds,
          stringify: StringTransformer.stringifyIds,
        },
        season__in: {
          parse: StringTransformer.parseStrings,
          stringify: StringTransformer.stringifyStrings,
        },
        query: {
          parse: StringTransformer.parseString,
          stringify: StringTransformer.stringifyString,
        },
        isPublished__eq: {
          parse: StringTransformer.parseBoolean,
          stringify: StringTransformer.stringifyBoolean,
        },
        isDeleted__eq: {
          parse: StringTransformer.parseBoolean,
          stringify: StringTransformer.stringifyBoolean,
        },
        pathpickOpen__eq: {
          parse: StringTransformer.parseBoolean,
          stringify: StringTransformer.stringifyBoolean,
        },
      },
    });

  const patch = useCallback(
    <T>(key: keyof T, value: T[keyof T]) => {
      set((prev) => {
        return {
          ...prev,
          [key]: value,
          pageNumber: 1,
        };
      });
    },
    [set]
  );

  return { value, patch, set, clear };
}
